import PropTypes from 'prop-types';
import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Formik, Form, Field } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import * as yup from 'yup';
import { injectIntl } from 'react-intl';
import QuestionField from '../GlobalCreate/QuestionField';
import Debug from '../../../../components/Debug';
import { UPDATE_GLOBAL_ARI } from '../mutations';
import messages from '../intl-messages';
import { withGrowl } from '../../../../components/GrowlNotifications';


const validationSchema = formatMessage => yup.object({
  question: yup.string()
    .max(250, formatMessage(messages.questionValidationMaxLength))
    .required(formatMessage(messages.questionValidationRequired)),
});

const EditQuestionDialog = ({
  isVisible, onHide, field, currentAccount, intl: { formatMessage }, growl,
}) => {
  const [updateGlobalAri] = useMutation(UPDATE_GLOBAL_ARI, { refetchQueries: ['getAccount'] });

  const initialValues = { question: field.question };

  return (
    <Dialog
      header={`${formatMessage(messages.editQuestionDialogHeader)}: "${field.question}"`}
      visible={isVisible}
      style={{ width: '50vw' }}
      onHide={onHide}
    >
      <Formik
        validationSchema={validationSchema(formatMessage)}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);

          const input = {
            accountId: currentAccount,
            ariFieldId: field.id,
            dataType: field.dataType,
            question: values.question,
            answers: field.answers,
            mandatory: field.mandatory,
            availableAccountsToEnable: field.availableAccountsToEnable.map(acc => acc.id),
          };

          updateGlobalAri({ variables: { input } })
            .then(() => {
              setSubmitting(false);
              onHide();
              growl.show({
                severity: 'success',
                sticky: false,
                summary: formatMessage(messages.growlAddFieldSuccessSummary),
                detail: formatMessage(messages.growlEditQuestionSuccessDetail),
              });
            })
            .catch(() => {
              setSubmitting(false);
              growl.show({
                severity: 'error',
                sticky: true,
                summary: formatMessage(messages.growlAddFieldFailSummary),
                detail: formatMessage(messages.growlEditQuestionFailDetail),
              });
            });
        }}
      >
        {({ isValid, isSubmitting }) => (
          <Form>
            <div className="p-grid" style={{ textAlign: 'center' }}>
              <div className="p-col-12">
                <Field
                  component={QuestionField}
                  name="question"
                  rows={5}
                  cols={25}
                  placeholder={formatMessage(messages.enterQuestionPlaceholder)}
                />
              </div>
              <div className="p-col-12">
                <Button
                  type="submit"
                  label={formatMessage(messages.updateLabel)}
                  icon="pi pi-check"
                  disabled={!isValid || isSubmitting}
                />
                <Button
                  label={formatMessage(messages.cancelLabel)}
                  icon="pi pi-times"
                  onClick={onHide}
                />
              </div>
            </div>
            <Debug />
          </Form>
        )}
      </Formik>

    </Dialog>
  );
};

EditQuestionDialog.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  isVisible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(EditQuestionDialog));
